import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { useToast } from '../hooks/use-toast';
import {
  PaperPlaneTilt, PencilSimple, ArrowClockwise, Eye, Clock, CheckCircle,
  XCircle, CircleNotch, Funnel, CaretDown, WarningCircle, FileText, Tag
} from '@phosphor-icons/react';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'text-gray-400 bg-gray-500/10 border-gray-500/20', icon: FileText },
  submitted: { label: 'Submitted', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', icon: PaperPlaneTilt },
  in_review: { label: 'In Review', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20', icon: Eye },
  approved: { label: 'Approved', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', icon: CheckCircle },
  published: { label: 'Published', color: 'text-green-400 bg-green-500/10 border-green-500/20', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'text-red-400 bg-red-500/10 border-red-500/20', icon: XCircle },
  unpublished: { label: 'Unpublished', color: 'text-gray-400 bg-gray-500/10 border-gray-500/20', icon: Clock },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = cfg.icon;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
      <Icon size={12} weight="bold" />
      {cfg.label}
    </span>
  );
};

const MySubmissions = () => {
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', short_description: '', category: '', tags: '', capabilities: '', integrations: '', difficulty_level: 'intermediate' });

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      const res = await fetch(`${BACKEND_URL}/api/marketplace/submissions?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error('Failed to fetch submissions', e); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleSubmitForReview = async (id) => {
    setActionLoading(id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/marketplace/submissions/${id}/submit`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        toast({ title: 'Submitted for Review', description: 'Your automation has been submitted for marketplace review.' });
        fetchSubmissions();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Failed to submit', variant: 'destructive' });
      }
    } catch { toast({ title: 'Error', description: 'Network error', variant: 'destructive' }); }
    finally { setActionLoading(null); }
  };

  const handleCreate = async (submitNow = false) => {
    setActionLoading('create');
    try {
      const body = {
        name: form.name,
        description: form.description,
        short_description: form.short_description,
        category: form.category,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        capabilities: form.capabilities ? form.capabilities.split(',').map(t => t.trim()).filter(Boolean) : [],
        integrations: form.integrations ? form.integrations.split(',').map(t => t.trim()).filter(Boolean) : [],
        difficulty_level: form.difficulty_level,
        submit_now: submitNow,
      };
      const res = await fetch(`${BACKEND_URL}/api/marketplace/submissions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast({ title: submitNow ? 'Submitted for Review' : 'Draft Saved', description: submitNow ? 'Your automation is now pending review.' : 'Draft saved successfully.' });
        setShowCreate(false);
        setForm({ name: '', description: '', short_description: '', category: '', tags: '', capabilities: '', integrations: '', difficulty_level: 'intermediate' });
        fetchSubmissions();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Failed to create', variant: 'destructive' });
      }
    } catch { toast({ title: 'Error', description: 'Network error', variant: 'destructive' }); }
    finally { setActionLoading(null); }
  };

  return (
    <div className="min-h-screen bg-[var(--surface-0)] p-6" data-testid="my-submissions-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">My Submissions</h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">Submit automations for marketplace review and track their status.</p>
          </div>
          <button
            data-testid="new-submission-btn"
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors"
          >
            New Submission
          </button>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3 mb-6">
          <Funnel size={16} className="text-[var(--text-muted)]" />
          <select
            data-testid="status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 bg-[var(--surface-2)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-primary)]"
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <span className="text-xs text-[var(--text-muted)]">{total} submission{total !== 1 ? 's' : ''}</span>
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
            <div className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl w-full max-w-lg p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="create-submission-modal">
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">New Automation Submission</h2>
              <div className="space-y-3">
                <input data-testid="sub-name" placeholder="Automation Name *" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="w-full px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--brand-primary)]" />
                <textarea data-testid="sub-desc" placeholder="Description" rows={3} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--brand-primary)] resize-none" />
                <input data-testid="sub-short-desc" placeholder="Short description" value={form.short_description} onChange={e => setForm(f => ({...f, short_description: e.target.value}))} className="w-full px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--brand-primary)]" />
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="sub-category" placeholder="Category" value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--brand-primary)]" />
                  <select data-testid="sub-difficulty" value={form.difficulty_level} onChange={e => setForm(f => ({...f, difficulty_level: e.target.value}))} className="px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-primary)]">
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                <input data-testid="sub-tags" placeholder="Tags (comma separated)" value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} className="w-full px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--brand-primary)]" />
                <input data-testid="sub-integrations" placeholder="Integrations (comma separated)" value={form.integrations} onChange={e => setForm(f => ({...f, integrations: e.target.value}))} className="w-full px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--brand-primary)]" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">Cancel</button>
                <button data-testid="save-draft-btn" onClick={() => handleCreate(false)} disabled={!form.name || actionLoading === 'create'} className="px-4 py-2 bg-[var(--surface-3)] text-[var(--text-primary)] rounded-lg text-sm font-medium border border-[var(--border-default)] hover:bg-[var(--surface-4)] transition-colors disabled:opacity-50">
                  {actionLoading === 'create' ? <CircleNotch size={14} className="animate-spin" /> : 'Save Draft'}
                </button>
                <button data-testid="submit-review-btn" onClick={() => handleCreate(true)} disabled={!form.name || actionLoading === 'create'} className="px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors disabled:opacity-50">
                  Submit for Review
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submissions List */}
        {loading ? (
          <div className="flex justify-center py-16"><CircleNotch size={28} className="animate-spin text-[var(--brand-primary)]" /></div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-16 bg-[var(--surface-1)] rounded-xl border border-[var(--border-default)]" data-testid="empty-submissions">
            <FileText size={40} className="mx-auto text-[var(--text-muted)] mb-4" />
            <h3 className="text-[var(--text-primary)] text-base font-semibold mb-1.5">You haven't submitted any apps yet</h3>
            <p className="text-[var(--text-muted)] text-sm mb-6 max-w-md mx-auto">Start by creating your first integration for the marketplace. Build something useful, submit it for review, and share it with the platform.</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setShowCreate(true)}
                className="px-5 py-2.5 bg-[var(--brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors"
                data-testid="empty-state-submit-btn"
              >
                Submit App
              </button>
              <button
                onClick={() => window.location.href = '/app-store'}
                className="px-5 py-2.5 border border-[var(--border-default)] text-[var(--text-secondary)] rounded-lg text-sm hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-colors"
                data-testid="empty-state-browse-btn"
              >
                Browse App Store
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="submissions-list">
            {submissions.map(sub => (
              <div
                key={sub.submission_id}
                data-testid={`submission-${sub.submission_id}`}
                className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-4 hover:border-[var(--border-strong)] transition-colors cursor-pointer"
                onClick={() => setSelectedSub(selectedSub?.submission_id === sub.submission_id ? null : sub)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">{sub.name}</h3>
                      <StatusBadge status={sub.status} />
                    </div>
                    <div className="flex items-center gap-3 mt-1.5">
                      {sub.category && <span className="text-xs text-[var(--text-muted)]">{sub.category}</span>}
                      <span className="text-xs text-[var(--text-muted)]">v{sub.version || 1}</span>
                      <span className="text-xs text-[var(--text-muted)]">Updated {new Date(sub.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {(sub.status === 'draft' || sub.status === 'rejected') && (
                      <button
                        data-testid={`submit-btn-${sub.submission_id}`}
                        onClick={(e) => { e.stopPropagation(); handleSubmitForReview(sub.submission_id); }}
                        disabled={actionLoading === sub.submission_id}
                        className="px-3 py-1.5 bg-[var(--brand-primary)] text-white rounded-lg text-xs font-medium hover:bg-[var(--brand-primary-hover)] transition-colors disabled:opacity-50"
                      >
                        {actionLoading === sub.submission_id ? <CircleNotch size={12} className="animate-spin" /> : (
                          <><PaperPlaneTilt size={12} className="inline mr-1" />{sub.status === 'rejected' ? 'Resubmit' : 'Submit'}</>
                        )}
                      </button>
                    )}
                    <CaretDown size={14} className={`text-[var(--text-muted)] transition-transform ${selectedSub?.submission_id === sub.submission_id ? 'rotate-180' : ''}`} />
                  </div>
                </div>

                {/* Expanded Detail */}
                {selectedSub?.submission_id === sub.submission_id && (
                  <div className="mt-4 pt-4 border-t border-[var(--border-default)] space-y-3" data-testid={`detail-${sub.submission_id}`}>
                    {sub.description && <p className="text-xs text-[var(--text-secondary)]">{sub.description}</p>}
                    {sub.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {sub.tags.map(t => <span key={t} className="px-2 py-0.5 bg-[var(--surface-3)] rounded text-[10px] text-[var(--text-muted)]">{t}</span>)}
                      </div>
                    )}
                    {sub.integrations?.length > 0 && (
                      <div className="text-xs text-[var(--text-muted)]">Integrations: {sub.integrations.join(', ')}</div>
                    )}

                    {/* Review Outcome — Prominent for rejected/approved/published */}
                    {sub.status === 'rejected' && sub.review_notes && (
                      <div className="p-3.5 bg-red-500/8 rounded-xl border border-red-500/15" data-testid={`review-outcome-${sub.submission_id}`}>
                        <div className="flex items-start gap-2.5">
                          <XCircle size={18} weight="fill" className="text-red-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-red-300 mb-1">Changes Requested</p>
                            <p className="text-xs text-red-200/80 leading-relaxed">{sub.review_notes}</p>
                            {sub.reviewed_at && <p className="text-[10px] text-red-300/50 mt-2">{new Date(sub.reviewed_at).toLocaleString()}</p>}
                            <button
                              data-testid={`resubmit-from-notes-${sub.submission_id}`}
                              onClick={(e) => { e.stopPropagation(); handleSubmitForReview(sub.submission_id); }}
                              disabled={actionLoading === sub.submission_id}
                              className="mt-2.5 px-3 py-1.5 bg-red-500/15 text-red-300 rounded-lg text-xs font-medium hover:bg-red-500/25 transition-colors border border-red-500/20"
                            >
                              {actionLoading === sub.submission_id ? <CircleNotch size={12} className="animate-spin" /> : 'Update & Resubmit'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {sub.status === 'approved' && (
                      <div className="p-3 bg-emerald-500/8 rounded-xl border border-emerald-500/15" data-testid={`review-outcome-${sub.submission_id}`}>
                        <div className="flex items-center gap-2">
                          <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                          <p className="text-xs text-emerald-300 font-medium">Approved — Ready to publish</p>
                        </div>
                        {sub.review_notes && <p className="text-xs text-emerald-200/60 mt-1 ml-6">{sub.review_notes}</p>}
                      </div>
                    )}
                    {sub.status === 'published' && (
                      <div className="p-3 bg-green-500/8 rounded-xl border border-green-500/15" data-testid={`review-outcome-${sub.submission_id}`}>
                        <div className="flex items-center gap-2">
                          <CheckCircle size={16} weight="fill" className="text-green-400" />
                          <p className="text-xs text-green-300 font-medium">Live on Marketplace</p>
                          {sub.published_at && <span className="text-[10px] text-green-300/50">since {new Date(sub.published_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                    )}
                    {(sub.status !== 'rejected' && sub.review_notes) && sub.status !== 'approved' && sub.status !== 'published' && (
                      <div className="p-3 bg-[var(--surface-2)] rounded-lg border border-[var(--border-default)]" data-testid={`review-notes-${sub.submission_id}`}>
                        <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase mb-1">Review Notes</p>
                        <p className="text-xs text-[var(--text-secondary)]">{sub.review_notes}</p>
                        {sub.reviewed_at && <p className="text-[10px] text-[var(--text-muted)] mt-1">{new Date(sub.reviewed_at).toLocaleString()}</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MySubmissions;
